import { phdScannerApiAxios } from '../lib/axios-interceptor';

export interface UniversityUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  university: string;
  position: string;
  department: string;
  emailVerified: boolean;
  lastLoginAt?: number;
  status: 'active' | 'inactive' | 'pending';
  createdAt: string;
  updatedAt: string;
  city?: string;
  country?: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface SignupRequest {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  university: string;
  position: string;
  department: string;
}

export interface AuthResponse {
  success: boolean;
  message: string;
  user?: UniversityUser;
  token?: string;
}

export interface VerificationRequest {
  token: string;
}

// Helper function to transform backend response to frontend format
const transformUserData = (backendUser: any): UniversityUser => {
  return {
    id: backendUser.id,
    email: backendUser.email,
    firstName: backendUser.first_name,
    lastName: backendUser.last_name,
    university: backendUser.university,
    position: backendUser.position,
    department: backendUser.department,
    emailVerified: backendUser.email_verified,
    lastLoginAt: backendUser.last_login_at,
    status: backendUser.status,
    createdAt: backendUser.created_at,
    updatedAt: backendUser.updated_at,
    city: backendUser.city,
    country: backendUser.country
  };
};

export const UniversityAuthService = {
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    try {
      const response = await phdScannerApiAxios.post('/api/university/auth/login', credentials);
      const data = response.data;
      // Transform user data if present
      if (data.user) {
        data.user = transformUserData(data.user);
      }
      return data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Login failed');
    }
  },

  async signup(userData: SignupRequest): Promise<AuthResponse> {
    try {
      // Transform camelCase to snake_case for backend compatibility
      const transformedData = {
        first_name: userData.firstName,
        last_name: userData.lastName,
        email: userData.email,
        password: userData.password,
        confirm_password: userData.confirmPassword,
        university: userData.university,
        position: userData.position,
        department: userData.department
      };
      
      const response = await phdScannerApiAxios.post('/api/university/auth/signup', transformedData);
      const data = response.data;
      // Transform user data if present
      if (data.user) {
        data.user = transformUserData(data.user);
      }
      return data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Signup failed');
    }
  },

  async verifyEmail(token: string): Promise<AuthResponse> {
    try {
      const response = await phdScannerApiAxios.post('/api/university/auth/verify', { token });
      const data = response.data;
      // Transform user data if present
      if (data.user) {
        data.user = transformUserData(data.user);
      }
      return data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Email verification failed');
    }
  },

  async resendVerification(email: string): Promise<AuthResponse> {
    try {
      const response = await phdScannerApiAxios.post('/api/university/auth/resend-verification', { email });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to resend verification email');
    }
  },

  async forgotPassword(email: string): Promise<AuthResponse> {
    try {
      const response = await phdScannerApiAxios.post('/api/university/auth/forgot-password', { email });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to send password reset email');
    }
  },

  async resetPassword(token: string, newPassword: string): Promise<AuthResponse> {
    try {
      const response = await phdScannerApiAxios.post('/api/university/auth/reset-password', { 
        token, 
        password: newPassword 
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Password reset failed');
    }
  },

  async getCurrentUser(): Promise<UniversityUser> {
    try {
      const response = await phdScannerApiAxios.get('/api/university/auth/me');
      return transformUserData(response.data);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to get user data');
    }
  },

  async updateProfile(profileData: Partial<UniversityUser>): Promise<UniversityUser> {
    try {
      // Transform camelCase to snake_case for backend compatibility
      const transformedData = {
        first_name: profileData.firstName,
        last_name: profileData.lastName,
        email: profileData.email,
        university: profileData.university,
        position: profileData.position,
        department: profileData.department,
        city: profileData.city,
        country: profileData.country
      };
      
      const response = await phdScannerApiAxios.put('/api/university/auth/profile', transformedData);
      return transformUserData(response.data);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update profile');
    }
  },

  async logout(): Promise<void> {
    try {
      await phdScannerApiAxios.post('/api/university/auth/logout');
      // Clear local storage
      localStorage.removeItem('university_user');
      localStorage.removeItem('university_token');
    } catch (error: any) {
      // Even if the API call fails, clear local storage
      localStorage.removeItem('university_user');
      localStorage.removeItem('university_token');
      throw new Error(error.response?.data?.message || 'Logout failed');
    }
  },

  // Helper method to check if user is authenticated
  isAuthenticated(): boolean {
    // Check for cookie-based authentication first
    const sessionCookie = this.getCookie('university_session_id');
    if (sessionCookie) {
      return true;
    }
    
    // Fallback to localStorage check
    const token = localStorage.getItem('university_token');
    const user = localStorage.getItem('university_user');
    return !!(token && user);
  },

  // Helper method to get cookie value
  getCookie(name: string): string | null {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(';').shift() || null;
    }
    return null;
  },

  // Helper method to get stored user data
  getStoredUser(): UniversityUser | null {
    const userData = localStorage.getItem('university_user');
    if (userData) {
      try {
        return JSON.parse(userData);
      } catch (error) {
        return null;
      }
    }
    return null;
  },

  // Helper method to get stored token
  getStoredToken(): string | null {
    return localStorage.getItem('university_token');
  },

  // Upload university logo
  async uploadLogo(logoFile: File): Promise<{ success: boolean; message: string; logo_url?: string; filename?: string }> {
    try {
      const formData = new FormData();
      formData.append('logo', logoFile);

      const response = await phdScannerApiAxios.post('/api/university/opportunities/upload-logo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return {
        success: true,
        message: response.data.message,
        logo_url: response.data.logo_url,
        filename: response.data.filename
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.error || 'Failed to upload logo'
      };
    }
  },

  // Get university logo URL
  getLogoUrl(universityName: string): string {
    // Return the full URL to the backend server for the general images endpoint
    const encodedUniversity = encodeURIComponent(universityName + '.png');
    return `${process.env.REACT_APP_API_URL || 'http://localhost:8181'}/api/images/university/${encodedUniversity}`;
  }
};
